import { forwardRef, useImperativeHandle, useRef } from "react";

export type StyleRange = { start: number; end: number; kind: "bold" | "italic" };

/**
 * Turns the composer's plain text back into the `**bold**` / `*italic*`
 * wire format every other part of the app already understands (sending,
 * scheduled sends, the message renderer). Ranges never overlap — bold and
 * italic are mutually exclusive "modes" here, never nested — so a single
 * left-to-right pass is enough.
 */
export function serializeMarkdown(plain: string, ranges: StyleRange[]): string {
  if (ranges.length === 0) return plain;
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  let out = "";
  let cursor = 0;
  for (const r of sorted) {
    if (r.start < cursor || r.end <= r.start) continue; // defensive: skip anything malformed
    out += plain.slice(cursor, r.start);
    const marker = r.kind === "bold" ? "**" : "*";
    out += marker + plain.slice(r.start, r.end) + marker;
    cursor = r.end;
  }
  out += plain.slice(cursor);
  return out;
}

export type RichComposerHandle = {
  focus: () => void;
  getCaret: () => number;
  /** Replaces [start, end) of the PLAIN text with `text`, always inserted
   * as plain unstyled content, then places the caret at `caretAfter`.
   * Used for anything that edits the draft from outside normal typing:
   * picking a mention, the "@"/emoji toolbar buttons, and pasted text. */
  replaceRange: (start: number, end: number, text: string, caretAfter: number) => void;
  /** Starts, closes, or switches a live bold/italic run at the caret, or
   * wraps the current selection immediately if there is one. */
  toggleMarker: (kind: "bold" | "italic") => void;
  /** Wipes the composer back to empty — call once a message actually sends. */
  clear: () => void;
  /** Rebuilds the composer's content from a `**bold**` / `*italic*`
   * string — used to restore a draft after a failed send. */
  setContent: (markdown: string) => void;
  /** Ends whichever bold/italic run is currently open, if any, without
   * touching the rest of the content — used when switching conversations,
   * so a mode left on in one chat doesn't silently keep extending into
   * the next one after the toolbar button itself resets. */
  endActiveMarker: () => void;
};

type Props = {
  placeholder: string;
  onChange: (plain: string, markdown: string, caret: number) => void;
  onActiveMarkerChange: (marker: "bold" | "italic" | null) => void;
  onEnter: () => void;
  /** Fired before Enter/Shift+Enter get their default handling, so the
   * mention picker can claim Up/Down/Enter/Tab/Escape first — same
   * precedence the old textarea's onKeyDown gave it. Call
   * e.preventDefault() to suppress this component's own handling. */
  onKeyDownBefore?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPasteImage: (file: File) => void;
};

const ZWSP = "​";

function isStyleEl(node: ChildNode): node is HTMLElement {
  return node.nodeName === "B" || node.nodeName === "I";
}

/** The inline "= 19" arithmetic ghost suggestion (see maybeShowGhost) is a
 * real DOM node so it's visible, but purely decorative — never part of
 * the actual message. readModel/getCaretOffset/setCaretOffset all skip
 * it entirely, as if it weren't there. */
function isGhostEl(node: ChildNode): boolean {
  return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).hasAttribute("data-dbm-ghost");
}

/** Reads the contentEditable's flat child-node structure (text nodes, <br>
 * line breaks, and <b>/<i> runs each holding a single text node) back into
 * a plain string plus the style ranges over it. Also strips any left-over
 * caret-anchor zero-width spaces and deletes any run that ends up empty —
 * the DOM is the source of truth once the user has typed, so this runs on
 * every input event, but it only ever WALKS the DOM; it never rebuilds it,
 * which is what keeps typing from fighting the browser's own caret
 * placement (the classic controlled-contentEditable cursor-jump bug). */
function readModel(root: HTMLElement): { plain: string; ranges: StyleRange[] } {
  let plain = "";
  const ranges: StyleRange[] = [];
  for (const node of Array.from(root.childNodes)) {
    if (isGhostEl(node)) {
      continue;
    } else if (node.nodeType === Node.TEXT_NODE) {
      plain += node.textContent || "";
    } else if (node.nodeName === "BR") {
      plain += "\n";
    } else if (isStyleEl(node)) {
      const text = (node.textContent || "").replace(new RegExp(ZWSP, "g"), "");
      if (text.length > 0) {
        ranges.push({ start: plain.length, end: plain.length + text.length, kind: node.nodeName === "B" ? "bold" : "italic" });
        plain += text;
      } else {
        node.remove();
      }
    } else {
      plain += node.textContent || "";
    }
  }
  return { plain, ranges };
}

function getCaretOffset(root: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const { startContainer, startOffset } = sel.getRangeAt(0);
  if (!root.contains(startContainer)) return 0;
  let offset = 0;
  for (const node of Array.from(root.childNodes)) {
    if (isGhostEl(node)) continue;
    if (node === startContainer) return offset + startOffset;
    if (node.contains(startContainer)) return offset + startOffset;
    if (node.nodeType === Node.TEXT_NODE) offset += (node.textContent || "").length;
    else if (node.nodeName === "BR") offset += 1;
    else offset += (node.textContent || "").replace(new RegExp(ZWSP, "g"), "").length;
  }
  return offset;
}

function placeCaretAt(node: Node, offset: number) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.setStart(node, offset);
  range.collapse(true);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function placeCaretAfterNode(node: Node) {
  const parent = node.parentNode;
  if (!parent) return;
  const idx = Array.prototype.indexOf.call(parent.childNodes, node) + 1;
  placeCaretAt(parent, idx);
}

function setCaretOffset(root: HTMLElement, target: number) {
  let remaining = target;
  for (const node of Array.from(root.childNodes)) {
    if (isGhostEl(node)) {
      continue;
    } else if (node.nodeType === Node.TEXT_NODE) {
      const len = (node.textContent || "").length;
      if (remaining <= len) return placeCaretAt(node, remaining);
      remaining -= len;
    } else if (node.nodeName === "BR") {
      if (remaining <= 0) return placeCaretAfterNode(node);
      remaining -= 1;
    } else if (isStyleEl(node)) {
      const child = node.firstChild;
      const len = child ? (child.textContent || "").length : 0;
      if (remaining <= len && child) return placeCaretAt(child, remaining);
      remaining -= len;
    }
  }
  // Ran out of content — land at the very end of the root.
  const last = root.lastChild;
  if (last && last.nodeType === Node.TEXT_NODE) placeCaretAt(last, (last.textContent || "").length);
  else placeCaretAt(root, root.childNodes.length);
}

/** Inserts `text` as a plain (unstyled) text node at the given offset,
 * splitting on newlines into real <br> elements so multi-line pasted text
 * reads back correctly. Returns the caret offset right after the insert. */
function insertPlainAt(root: HTMLElement, offset: number, text: string): number {
  setCaretOffset(root, offset);
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return offset;
  const range = sel.getRangeAt(0);
  range.deleteContents();
  const lines = text.split("\n");
  const frag = document.createDocumentFragment();
  lines.forEach((line, i) => {
    if (i > 0) frag.appendChild(document.createElement("br"));
    if (line) frag.appendChild(document.createTextNode(line));
  });
  const lastNode = frag.lastChild;
  range.insertNode(frag);
  if (lastNode) placeCaretAfterNode(lastNode);
  return offset + text.length;
}

/** Matches a single basic arithmetic operation ending exactly at the
 * caret — e.g. "12+7" or "3.5 * 2" — anchored so it only fires on a
 * clean standalone expression (start-of-line/whitespace before the first
 * number), not something like an order number "PO-2024/07". Deliberately
 * single-operation only, per the ask: no "2+3*4" chains. */
const GHOST_EXPR_RE = /(?:^|\s)(-?\d+(?:\.\d+)?)\s*([+\-*/])\s*(-?\d+(?:\.\d+)?)$/;

/** Returns the " = 19" suggestion text for whatever expression ends at
 * the caret, or null if there's nothing to suggest (no match, divide by
 * zero, or a non-finite result). */
function computeGhostSuggestion(textBeforeCaret: string): string | null {
  const m = GHOST_EXPR_RE.exec(textBeforeCaret);
  if (!m) return null;
  const a = Number(m[1]);
  const op = m[2];
  const b = Number(m[3]);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  let result: number;
  if (op === "+") result = a + b;
  else if (op === "-") result = a - b;
  else if (op === "*") result = a * b;
  else if (b === 0) return null;
  else result = a / b;
  if (!Number.isFinite(result)) return null;
  // Rounded to avoid floating-point noise (0.1+0.2 !== 0.3) without
  // truncating a genuinely long decimal answer down to nothing useful.
  const rounded = Math.round(result * 1e6) / 1e6;
  return ` = ${rounded}`;
}

/** Removes the plain-text range [start, end) from the composer's content.
 * Only ever used on plain (unstyled) spans of text — mentions and pasted
 * text never land inside a bold/italic run in practice, so a naive
 * character walk is enough here. */
function deletePlainRange(root: HTMLElement, start: number, end: number) {
  setCaretOffset(root, start);
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return;
  // Cloned immediately — getRangeAt can return a live reference, and the
  // second setCaretOffset call below replaces the selection's own range,
  // which would otherwise retroactively mutate this one too.
  const startRange = sel.getRangeAt(0).cloneRange();
  setCaretOffset(root, end);
  const endSel = window.getSelection();
  if (!endSel || endSel.rangeCount === 0) return;
  const endRange = endSel.getRangeAt(0).cloneRange();
  const range = document.createRange();
  range.setStart(startRange.startContainer, startRange.startOffset);
  range.setEnd(endRange.startContainer, endRange.startOffset);
  range.deleteContents();
}

/**
 * The message composer, rebuilt as a real (if deliberately small) rich-text
 * editor so Bold/Italic behave like Teams/Slack: click Bold, type, and the
 * text itself looks bold immediately — no `**`/`*` characters ever appear,
 * either before or after sending.
 *
 * The previous version stored the draft as one plain string containing
 * literal markdown (`**like this**`) and only ever rendered it as flat
 * text in the box, which is why the asterisks were visible while typing —
 * a plain <textarea> is physically incapable of showing part of its own
 * value in a different font weight. Genuine live formatting needs a
 * contentEditable element instead.
 *
 * Design: rather than a full rich-text document model, the DOM itself IS
 * the model — always a flat list of text nodes, <br> line breaks, and
 * <b>/<i> elements each holding exactly one text node (never nested, never
 * containing a <br>). Toggling Bold/Italic inserts a real <b>/<i> element
 * at the caret and lets the browser's native typing extend it — which is
 * what avoids the classic "controlled contentEditable" cursor-jump bug:
 * this component never rebuilds the DOM out from under an in-progress
 * keystroke, it only WALKS it (in readModel) to report the plain text and
 * style ranges back to the parent. The DOM is only rebuilt for external,
 * one-off changes (see the ref methods), never for normal typing.
 */
const RichComposer = forwardRef<RichComposerHandle, Props>(function RichComposer(
  { placeholder, onChange, onActiveMarkerChange, onEnter, onKeyDownBefore, onPasteImage },
  ref
) {
  const rootRef = useRef<HTMLDivElement>(null);
  const activeMarkerRef = useRef<"bold" | "italic" | null>(null);
  const activeSpanRef = useRef<HTMLElement | null>(null);
  // The faint "= 19" arithmetic preview — see computeGhostSuggestion.
  // Re-evaluated on every keystroke (cleared first, then possibly
  // reinserted), so it only ever reflects what's true right now, never a
  // stale suggestion from a prior character.
  const ghostRef = useRef<HTMLSpanElement | null>(null);

  const clearGhost = () => {
    if (ghostRef.current) {
      ghostRef.current.remove();
      ghostRef.current = null;
    }
  };

  /** Inserts a non-editable, decoration-only suggestion span right at the
   * caret if the text just before it forms a basic arithmetic expression.
   * Purely visual — never touches `plain`/`markdown`/caret, and readModel
   * etc. all skip it via isGhostEl. Must run AFTER fireChange has already
   * reported the real change, so the suggestion is never mistaken for
   * part of the message. */
  const maybeShowGhost = (root: HTMLElement, plain: string, caret: number) => {
    const suggestion = computeGhostSuggestion(plain.slice(0, caret));
    if (!suggestion) return;
    setCaretOffset(root, caret);
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const span = document.createElement("span");
    span.setAttribute("data-dbm-ghost", "1");
    span.setAttribute("contenteditable", "false");
    span.className = "composer-ghost";
    span.textContent = suggestion;
    sel.getRangeAt(0).insertNode(span);
    ghostRef.current = span;
    setCaretOffset(root, caret); // insertNode can shift the live selection — put it back
  };

  const fireChange = () => {
    const root = rootRef.current;
    if (!root) return;
    clearGhost();
    const { plain, ranges } = readModel(root);
    // Deleting the last character sometimes leaves a stray empty text
    // node or <br> behind (browser-dependent), which would stop the
    // `:empty` placeholder from reappearing even though there's nothing
    // left to show.
    if (!plain && root.childNodes.length > 0) root.innerHTML = "";
    const caret = getCaretOffset(root);
    onChange(plain, serializeMarkdown(plain, ranges), caret);
    maybeShowGhost(root, plain, caret);
  };

  /** Ends whichever run is currently open (if any) — drops it entirely if
   * nothing was typed inside, otherwise just stops extending it. */
  const closeActiveMarker = () => {
    const span = activeSpanRef.current;
    activeMarkerRef.current = null;
    activeSpanRef.current = null;
    onActiveMarkerChange(null);
    if (!span) return;
    const text = span.textContent?.replace(new RegExp(ZWSP, "g"), "") || "";
    if (!text) {
      const after = span.nextSibling;
      span.remove();
      if (after) placeCaretAt(after.parentNode!, Array.prototype.indexOf.call(after.parentNode!.childNodes, after));
    } else {
      placeCaretAfterNode(span);
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => rootRef.current?.focus(),
    getCaret: () => (rootRef.current ? getCaretOffset(rootRef.current) : 0),
    replaceRange: (start, end, text, caretAfter) => {
      const root = rootRef.current;
      if (!root) return;
      root.focus();
      if (end > start) deletePlainRange(root, start, end);
      insertPlainAt(root, start, text);
      setCaretOffset(root, caretAfter);
      fireChange();
    },
    toggleMarker: (kind) => {
      const root = rootRef.current;
      if (!root) return;
      root.focus();
      const sel = window.getSelection();
      const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
      const hasSelection = !!range && !range.collapsed && root.contains(range.startContainer);

      if (hasSelection && range) {
        if (activeMarkerRef.current) closeActiveMarker();
        const el = document.createElement(kind === "bold" ? "b" : "i");
        const frag = range.extractContents();
        el.textContent = frag.textContent || "";
        range.insertNode(el);
        placeCaretAfterNode(el);
        fireChange();
        return;
      }

      if (activeMarkerRef.current === kind) {
        closeActiveMarker();
        fireChange();
        return;
      }
      if (activeMarkerRef.current) closeActiveMarker();

      const el = document.createElement(kind === "bold" ? "b" : "i");
      const anchor = document.createTextNode(ZWSP);
      el.appendChild(anchor);
      const insertRange = window.getSelection()?.rangeCount ? window.getSelection()!.getRangeAt(0) : null;
      if (insertRange) {
        insertRange.deleteContents();
        insertRange.insertNode(el);
      } else {
        root.appendChild(el);
      }
      placeCaretAt(anchor, 1);
      activeMarkerRef.current = kind;
      activeSpanRef.current = el;
      onActiveMarkerChange(kind);
      fireChange();
    },
    clear: () => {
      const root = rootRef.current;
      if (!root) return;
      root.innerHTML = "";
      activeMarkerRef.current = null;
      activeSpanRef.current = null;
      ghostRef.current = null;
      onActiveMarkerChange(null);
    },
    setContent: (markdown) => {
      const root = rootRef.current;
      if (!root) return;
      root.innerHTML = "";
      const appendPlain = (text: string) => {
        text.split("\n").forEach((line, i) => {
          if (i > 0) root.appendChild(document.createElement("br"));
          if (line) root.appendChild(document.createTextNode(line));
        });
      };
      const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
      let last = 0;
      let m: RegExpExecArray | null;
      while ((m = re.exec(markdown)) !== null) {
        if (m.index > last) appendPlain(markdown.slice(last, m.index));
        const el = document.createElement(m[1] !== undefined ? "b" : "i");
        el.textContent = m[1] !== undefined ? m[1] : m[2];
        root.appendChild(el);
        last = m.index + m[0].length;
      }
      appendPlain(markdown.slice(last));
      activeMarkerRef.current = null;
      activeSpanRef.current = null;
      onActiveMarkerChange(null);
      fireChange();
    },
    endActiveMarker: () => {
      if (activeMarkerRef.current) closeActiveMarker();
    },
  }));

  return (
    <div
      ref={rootRef}
      className="composer-rich"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label="Message"
      aria-multiline="true"
      data-placeholder={placeholder}
      onInput={fireChange}
      onBlur={clearGhost}
      onKeyDown={(e) => {
        onKeyDownBefore?.(e);
        if (e.defaultPrevented) return;
        // Accepts the "= 19" arithmetic ghost suggestion, if one's showing
        // right now — checked before Enter/Shift+Enter since Tab isn't
        // either of those anyway, but ordered first for clarity. Falls
        // through to the browser's normal Tab behavior (move focus along)
        // when there's no suggestion to accept.
        if (e.key === "Tab" && ghostRef.current) {
          e.preventDefault();
          const root = rootRef.current;
          const span = ghostRef.current;
          const text = span.textContent || "";
          ghostRef.current = null;
          span.remove();
          if (root) {
            const caret = getCaretOffset(root);
            const next = insertPlainAt(root, caret, text);
            setCaretOffset(root, next);
          }
          fireChange();
          return;
        }
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onEnter();
          return;
        }
        if (e.key === "Enter" && e.shiftKey) {
          e.preventDefault();
          // A manual line break always ends whichever run is open — bold
          // or italic mode doesn't carry across a line the way it doesn't
          // carry across sending, keeping "where does styling end" simple
          // and predictable rather than something you have to remember.
          if (activeMarkerRef.current) closeActiveMarker();
          const root = rootRef.current;
          const sel = window.getSelection();
          if (root && sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            const br = document.createElement("br");
            range.insertNode(br);
            placeCaretAfterNode(br);
          }
          fireChange();
        }
      }}
      onPaste={(e) => {
        e.preventDefault();
        const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith("image/"));
        const file = item?.getAsFile();
        if (file) {
          onPasteImage(file);
          return;
        }
        const text = e.clipboardData.getData("text/plain");
        if (!text) return;
        const root = rootRef.current;
        if (!root) return;
        const caret = getCaretOffset(root);
        const next = insertPlainAt(root, caret, text);
        setCaretOffset(root, next);
        fireChange();
      }}
    />
  );
});

export default RichComposer;
